<script lang="ts">
	import { ControlsContainer, Slider, SliderV2 } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Components
	import ControlOnOff from '../../Controls/Single/ControlOnOff.svelte';
	import InputsReaderVisualizer from '../../Controls/Indicators/InputsReaderVisualizer.svelte';
	import InputsReaderVisualizerV2 from '../../Controls/Indicators/InputsReaderVisualizerV2.svelte';
	//Types
	import type { Device } from 'src/types/Device';

	export let device: Device;
	export let useV2Components = false;

	const component = device?.component || '';

	let isLoading = false;
	let valueBeforeSend: number = null;

	$: status = devices_status.getByRealId(device.id);
	$: settings = devices_settings.getByRealId(device.id);
	$: componentType = component.split(':')?.[0];
	$: componentIndex = Number(component.split(':')?.[1] ?? device?.channel ?? 0);
	$: isLightComponent = /^(light|cct|rgb|rgbw)$/.test(componentType);
	$: brightnessValue = getBrightnessValue(status, component, componentIndex);
	$: value = valueBeforeSend ?? brightnessValue;
	$: SliderComponent = useV2Components ? SliderV2 : Slider;

	const a11yProps = {
		ariaLabel: $format('control_slider', { default: 'Control slider' }),
		ariaLabelValue: $format('control_slider', { default: 'Control slider' }),
		sliderAriaLabel: $format('control_slider', { default: 'Control slider' }),
	};

	const getBrightnessValue = (status: any, component: string, componentIndex: number) => {
		const value =
			status?.[`light:${componentIndex}`]?.brightness ?? status?.[component]?.brightness ?? 0;
		return Number.isFinite(Number(value)) ? Number(value) : 0;
	};

	const changeBrightness = async (value: number) => {
		const brightness = Number(value);
		if (!Number.isFinite(brightness)) return;

		isLoading = true;
		valueBeforeSend = brightness;

		const nextStatus = {
			...status,
			[component]: {
				...(status?.[component] || {}),
				brightness,
			},
		};

		const res = await gen2Request(device, `${componentType}.set`, {
			id: componentIndex,
			brightness,
		});

		if (res?.isok) {
			devices_status.update_device_status({
				id: real_id(device.id),
				msg: nextStatus,
			});
		} else {
			valueBeforeSend = null;
		}
		isLoading = false;
	};
</script>

{#if /^(relay|light)$/.test(device?.category)}
	<div class="flex-full-height justify-content-end">
		{#if isLightComponent}
			<svelte:component
				this={SliderComponent}
				id="slider-xt1-brightness"
				class={useV2Components
					? 'flex-full-height justify-content-center'
					: 'flex-full-height mb-l'}
				title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
				{value}
				type="vertical"
				min={0}
				max={100}
				step={1}
				{a11yProps}
				on:value_change={(e) => changeBrightness(e.detail?.value)}
				{isLoading}
			/>
		{/if}
		<ControlOnOff {device} {status} {settings} isWide {useV2Components} />
	</div>
{:else if device?.category === 'inputs_reader'}
	<div class="flex-full-height justify-content-end">
		<ControlsContainer color={useV2Components ? 'color-0' : undefined}>
			<svelte:fragment slot="first">
				{#if useV2Components}
					<InputsReaderVisualizerV2 {device} {status} {settings} isInCard={false} />
				{:else}
					<InputsReaderVisualizer {device} {status} {settings} isWide isInCard={false} />
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	</div>
{/if}
